import { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import type { HeatmapCell } from '@/types/analytics';
import styles from './HeatmapChart.module.scss';

export interface HeatmapChartProps {
  data: HeatmapCell[];
  width: number;
  height: number;
  cellSize?: number;
  cellGap?: number;
  colorScale?: [string, string]; // [minColor, maxColor]
  showMonthLabels?: boolean;
  showWeekdayLabels?: boolean;
  animate?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Reusable D3 Calendar Heatmap Component
 *
 * GitHub-style contribution calendar showing activity over time.
 * Each cell represents a day with color intensity based on value.
 *
 * @example
 * <HeatmapChart
 *   data={[
 *     { date: '2024-01-01', value: 5, label: '5 sessions' },
 *     { date: '2024-01-02', value: 3, label: '3 sessions' }
 *   ]}
 *   width={800}
 *   height={120}
 *   showMonthLabels
 *   showWeekdayLabels
 * />
 */
export function HeatmapChart({
  data,
  width,
  height,
  cellSize = 12,
  cellGap = 2,
  colorScale = ['var(--surface)', 'var(--primary)'],
  showMonthLabels = true,
  showWeekdayLabels = true,
  animate = true,
  minDate: propMinDate,
  maxDate: propMaxDate,
}: HeatmapChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || width === 0 || height === 0) {
      return;
    }

    // Clear previous chart
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Parse dates and create data map
    const dataMap = new Map(
      data.map((d) => [d.date, { value: d.value, label: d.label, color: d.color }])
    );

    // Get date range
    const dates = data.map((d) => new Date(d.date));
    const minDate = propMinDate || d3.min(dates) || new Date();
    const maxDate = propMaxDate || d3.max(dates) || new Date();

    // Generate all dates in range
    const allDates: Date[] = [];
    let currentDate = new Date(minDate);
    while (currentDate <= maxDate) {
      allDates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Calculate number of weeks
    // const weeks = Math.ceil(allDates.length / 7);

    // Adjust margins for labels
    const margin = {
      top: showMonthLabels ? 20 : 5,
      right: 5,
      bottom: 5,
      left: showWeekdayLabels ? 30 : 5,
    };

    // Create chart group
    const chart = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Color scale
    const maxValue = d3.max(data, (d) => d.value) || 10;
    const color = d3
      .scaleLinear<string>()
      .domain([0, maxValue])
      .range(colorScale);

    // Month labels
    if (showMonthLabels) {
      const months = d3.timeMonths(minDate, maxDate);
      months.forEach((month) => {
        const weekIndex = d3.timeSunday.count(d3.timeSunday.floor(minDate), month);
        const x = weekIndex * (cellSize + cellGap);

        chart
          .append('text')
          .attr('class', styles.heatmapChart__monthLabel)
          .attr('x', x)
          .attr('y', -5)
          .text(d3.timeFormat('%b')(month));
      });
    }

    // Weekday labels
    if (showWeekdayLabels) {
      const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      weekdays.forEach((day, i) => {
        if (i % 2 === 1) {
          // Only show alternate days
          chart
            .append('text')
            .attr('class', styles.heatmapChart__weekdayLabel)
            .attr('x', -5)
            .attr('y', i * (cellSize + cellGap) + cellSize / 2)
            .attr('text-anchor', 'end')
            .attr('dominant-baseline', 'middle')
            .text(day);
        }
      });
    }

    // Draw cells
    allDates.forEach((date) => {
      const dateStr = d3.timeFormat('%Y-%m-%d')(date);
      const cellData = dataMap.get(dateStr);
      const value = cellData?.value || 0;
      const weekIndex = d3.timeSunday.count(minDate, date);
      const dayOfWeek = date.getDay();

      const cell = chart
        .append('rect')
        .attr('class', styles.heatmapChart__cell)
        .attr('x', weekIndex * (cellSize + cellGap))
        .attr('y', dayOfWeek * (cellSize + cellGap))
        .attr('width', cellSize)
        .attr('height', cellSize)
        .attr('rx', 2)
        .attr('fill', cellData?.color || color(value))
        .attr('data-date', dateStr)
        .attr('data-value', value);

      // Tooltip
      if (cellData?.label) {
        const tooltipDate = d3.timeFormat('%m/%d/%Y')(date);
        cell.append('title').text(`${tooltipDate}\n${cellData.label}`);
      } else {
        const tooltipDate = d3.timeFormat('%m/%d/%Y')(date);
        cell.append('title').text(`${tooltipDate}: No activity`);
      }

      // Animation
      if (animate) {
        cell.attr('opacity', 0).transition().duration(50).delay(weekIndex * 10).attr('opacity', 1);
      }
    });
  }, [data, width, height, cellSize, cellGap, colorScale, showMonthLabels, showWeekdayLabels, animate]);

  if (width === 0 || height === 0) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={styles.heatmapChart}
      role="img"
      aria-label="Activity heatmap visualization"
    />
  );
}
